import {
  Check,
  CheckCircle2,
  Circle,
  Clock3,
  ListChecks,
  LoaderCircle,
  Plus,
  RotateCcw,
  UserRoundCheck,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  api,
  type CrmPatient,
  type CrmTask,
  type CrmTaskPriority,
  type CrmTaskStatus,
  type CrmTeamMember,
} from "./api";
import { useCrm } from "./crm-context";

const FILTERS: Array<{ id: "ALL" | CrmTaskStatus; label: string }> = [
  { id: "ALL", label: "Todas" },
  { id: "OPEN", label: "Pendientes" },
  { id: "IN_PROGRESS", label: "En curso" },
  { id: "COMPLETED", label: "Completadas" },
];

const statusLabel: Record<CrmTaskStatus, string> = {
  OPEN: "Pendiente",
  IN_PROGRESS: "En curso",
  COMPLETED: "Completada",
  CANCELLED: "Cancelada",
};

const priorityLabel: Record<CrmTaskPriority, string> = {
  LOW: "Baja",
  NORMAL: "Normal",
  HIGH: "Alta",
};

function messageFor(reason: unknown, fallback: string): string {
  return reason instanceof Error ? reason.message : fallback;
}

function isManager(role: string | undefined): boolean {
  return role === "OWNER" || role === "ADMIN";
}

function formatDueAt(value: string | null): string {
  if (!value) return "Sin vencimiento";
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function isOverdue(task: CrmTask): boolean {
  return Boolean(
    task.dueAt &&
    new Date(task.dueAt).getTime() < Date.now() &&
    !["COMPLETED", "CANCELLED"].includes(task.status),
  );
}

export function TasksScreen(): ReactNode {
  const { activeOrganization } = useCrm();
  const [tasks, setTasks] = useState<CrmTask[]>([]);
  const [members, setMembers] = useState<CrmTeamMember[]>([]);
  const [patients, setPatients] = useState<CrmPatient[]>([]);
  const [filter, setFilter] = useState<"ALL" | CrmTaskStatus>("ALL");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<CrmTaskPriority>("NORMAL");
  const [dueAt, setDueAt] = useState("");
  const [petId, setPetId] = useState("");
  const [assignedUserId, setAssignedUserId] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const manager = isManager(activeOrganization?.role);
  const canCreate = activeOrganization?.role !== "AUDITOR";

  const load = useCallback(async () => {
    if (!activeOrganization) return;
    setLoading(true);
    setError(null);
    try {
      const [nextTasks, team, nextPatients] = await Promise.all([
        api.tasks(activeOrganization.id, filter === "ALL" ? undefined : filter),
        api.team(activeOrganization.id),
        api.patients(activeOrganization.id),
      ]);
      setTasks(nextTasks);
      setMembers(team.members);
      setPatients(nextPatients);
    } catch (reason) {
      setError(messageFor(reason, "No pudimos cargar las tareas internas."));
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.id, filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const actionableCount = useMemo(
    () => tasks.filter((task) => ["OPEN", "IN_PROGRESS"].includes(task.status)).length,
    [tasks],
  );

  function resetForm(): void {
    setTitle("");
    setDescription("");
    setPriority("NORMAL");
    setDueAt("");
    setPetId("");
    setAssignedUserId("");
    setShowForm(false);
  }

  async function createTask(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!activeOrganization || !canCreate || !title.trim()) return;
    setPendingId("new");
    setError(null);
    setNotice(null);
    try {
      const created = await api.createTask(activeOrganization.id, {
        title: title.trim(),
        description: description.trim() || null,
        priority,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
        ...(petId ? { petId } : {}),
        ...(manager && assignedUserId ? { assignedUserId } : {}),
      });
      if (filter === "ALL" || filter === created.status)
        setTasks((current) => [created, ...current]);
      setNotice(`“${created.title}” quedó registrada para el equipo.`);
      resetForm();
    } catch (reason) {
      setError(messageFor(reason, "No pudimos crear la tarea."));
    } finally {
      setPendingId(null);
    }
  }

  async function updateTask(
    task: CrmTask,
    input: Parameters<typeof api.updateTask>[2],
  ): Promise<void> {
    if (!activeOrganization) return;
    setPendingId(task.id);
    setError(null);
    setNotice(null);
    try {
      const updated = await api.updateTask(activeOrganization.id, task.id, input);
      setTasks((current) =>
        filter !== "ALL" && filter !== updated.status
          ? current.filter((item) => item.id !== updated.id)
          : current.map((item) => (item.id === updated.id ? updated : item)),
      );
      setNotice(
        input.status === "COMPLETED"
          ? `“${updated.title}” quedó completada.`
          : input.status === "IN_PROGRESS"
            ? `“${updated.title}” está en curso.`
            : `La tarea se actualizó.`,
      );
    } catch (reason) {
      setError(messageFor(reason, "No pudimos actualizar la tarea."));
    } finally {
      setPendingId(null);
    }
  }

  if (!activeOrganization) return null;

  return (
    <div className="screen tasks-screen">
      <header className="page-header">
        <div>
          <span className="eyebrow">Coordinación operativa</span>
          <h1>Tareas</h1>
          <p>
            Seguimientos internos con responsable y vencimiento, sin mezclar información clínica.
          </p>
        </div>
        {canCreate && (
          <button className="button button-primary" type="button" onClick={() => setShowForm(true)}>
            <Plus size={17} /> Nueva tarea
          </button>
        )}
      </header>

      {error && (
        <div className="error-panel" role="alert">
          <ListChecks size={20} aria-hidden="true" />
          <div>
            <strong>No pudimos completar la operación</strong>
            <p>{error}</p>
          </div>
          <button
            className="button button-secondary button-compact"
            type="button"
            onClick={() => void load()}
          >
            Reintentar
          </button>
        </div>
      )}
      {notice && (
        <div className="success-panel" role="status">
          <Check size={18} aria-hidden="true" />
          <span>{notice}</span>
          <button
            className="icon-button"
            type="button"
            onClick={() => setNotice(null)}
            aria-label="Cerrar aviso"
          >
            ×
          </button>
        </div>
      )}

      <section className="task-overview" aria-label="Resumen de tareas">
        <span className="task-overview-icon" aria-hidden="true">
          <ListChecks size={21} />
        </span>
        <div>
          <strong>
            {actionableCount === 1
              ? "1 tarea por resolver"
              : `${actionableCount} tareas por resolver`}
          </strong>
          <p>
            Las tareas no cambian la historia clínica; solo ordenan seguimiento, coordinación y
            recepción.
          </p>
        </div>
        <span className="task-overview-note">
          <Clock3 size={16} aria-hidden="true" /> Seguimiento del equipo
        </span>
      </section>

      {showForm && (
        <form className="task-editor panel" onSubmit={(event) => void createTask(event)}>
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Nueva coordinación</span>
              <h2>Registrar tarea interna</h2>
            </div>
            <button
              className="icon-button"
              type="button"
              onClick={resetForm}
              aria-label="Cerrar formulario de tarea"
            >
              ×
            </button>
          </div>
          <div className="form-grid">
            <label className="form-span-2" htmlFor="task-title">
              Tarea
              <input
                id="task-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                minLength={2}
                maxLength={160}
                autoFocus
                required
                placeholder="Ej. Llamar por resultado de laboratorio"
              />
            </label>
            <label className="form-span-2" htmlFor="task-patient">
              Mascota vinculada
              <select
                id="task-patient"
                value={petId}
                onChange={(event) => setPetId(event.target.value)}
              >
                <option value="">Sin paciente vinculado</option>
                {patients.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.name} · {patient.species === "DOG" ? "Perro" : "Gato"}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="task-priority">
              Prioridad
              <select
                id="task-priority"
                value={priority}
                onChange={(event) => setPriority(event.target.value as CrmTaskPriority)}
              >
                <option value="LOW">Baja</option>
                <option value="NORMAL">Normal</option>
                <option value="HIGH">Alta</option>
              </select>
            </label>
            <label htmlFor="task-due-at">
              Vence el
              <input
                id="task-due-at"
                type="datetime-local"
                value={dueAt}
                onChange={(event) => setDueAt(event.target.value)}
              />
            </label>
            {manager && (
              <label className="form-span-2" htmlFor="task-assignee">
                Responsable
                <select
                  id="task-assignee"
                  value={assignedUserId}
                  onChange={(event) => setAssignedUserId(event.target.value)}
                >
                  <option value="">Sin asignar</option>
                  {members
                    .filter((member) => member.role !== "AUDITOR")
                    .map((member) => (
                      <option key={member.userId} value={member.userId}>
                        {member.displayName} · {member.role}
                      </option>
                    ))}
                </select>
              </label>
            )}
            <label className="form-span-2" htmlFor="task-description">
              Contexto para el equipo
              <textarea
                id="task-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                maxLength={2000}
                rows={3}
                placeholder="Opcional. No incluyas información clínica sensible."
              />
            </label>
          </div>
          <div className="form-actions">
            <button
              className="button button-secondary"
              type="button"
              onClick={resetForm}
              disabled={pendingId !== null}
            >
              Cancelar
            </button>
            <button className="button button-primary" type="submit" disabled={pendingId !== null}>
              {pendingId ? <LoaderCircle className="spin" size={17} /> : <Check size={17} />}
              Crear tarea
            </button>
          </div>
        </form>
      )}

      <nav className="task-filters" aria-label="Filtrar tareas por estado">
        {FILTERS.map((item) => (
          <button
            className={filter === item.id ? "is-active" : undefined}
            key={item.id}
            type="button"
            aria-pressed={filter === item.id}
            onClick={() => setFilter(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {loading ? (
        <div className="list-skeleton task-skeleton" role="status" aria-label="Cargando tareas">
          <span />
          <span />
          <span />
        </div>
      ) : tasks.length ? (
        <section className="tasks-list" aria-label="Tareas internas">
          {tasks.map((task) => {
            const busy = pendingId === task.id;
            const overdue = isOverdue(task);
            return (
              <article className={`task-card task-${task.status.toLowerCase()}`} key={task.id}>
                <div className="task-card-main">
                  <span className="task-status-icon" aria-hidden="true">
                    {task.status === "COMPLETED" ? (
                      <CheckCircle2 size={20} />
                    ) : (
                      <Circle size={20} />
                    )}
                  </span>
                  <div>
                    <div className="task-card-title-row">
                      <h2>{task.title}</h2>
                      <span
                        className={`task-priority task-priority-${task.priority.toLowerCase()}`}
                      >
                        {priorityLabel[task.priority]}
                      </span>
                      <span className={`status task-status-${task.status.toLowerCase()}`}>
                        {statusLabel[task.status]}
                      </span>
                    </div>
                    {task.description && <p>{task.description}</p>}
                    <div className="task-meta">
                      <span className={overdue ? "is-overdue" : undefined}>
                        <Clock3 size={14} aria-hidden="true" /> {overdue ? "Vencida · " : ""}
                        {formatDueAt(task.dueAt)}
                      </span>
                      <span>
                        <UserRoundCheck size={14} aria-hidden="true" />{" "}
                        {task.assignedName ?? "Sin responsable"}
                      </span>
                      {task.pet && <span>Paciente: {task.pet.name}</span>}
                    </div>
                  </div>
                </div>
                <div className="task-card-actions">
                  {task.capabilities.canAssign && (
                    <label className="task-assignment">
                      <span className="sr-only">Responsable de {task.title}</span>
                      <select
                        value={task.assignedUserId ?? ""}
                        disabled={busy}
                        onChange={(event) =>
                          void updateTask(task, { assignedUserId: event.target.value || null })
                        }
                      >
                        <option value="">Sin asignar</option>
                        {members
                          .filter((member) => member.role !== "AUDITOR")
                          .map((member) => (
                            <option key={member.userId} value={member.userId}>
                              {member.displayName}
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                  {task.capabilities.canTransition && task.status === "OPEN" && (
                    <button
                      className="button button-secondary button-compact"
                      type="button"
                      disabled={busy}
                      onClick={() => void updateTask(task, { status: "IN_PROGRESS" })}
                    >
                      <RotateCcw size={16} /> Iniciar
                    </button>
                  )}
                  {task.capabilities.canTransition && task.status === "IN_PROGRESS" && (
                    <button
                      className="button button-primary button-compact"
                      type="button"
                      disabled={busy}
                      onClick={() => void updateTask(task, { status: "COMPLETED" })}
                    >
                      {busy ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}{" "}
                      Completar
                    </button>
                  )}
                  {task.capabilities.canEdit && ["OPEN", "IN_PROGRESS"].includes(task.status) && (
                    <button
                      className="button-link task-cancel"
                      type="button"
                      disabled={busy}
                      onClick={() => void updateTask(task, { status: "CANCELLED" })}
                    >
                      <XCircle size={16} /> Cancelar
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      ) : (
        <section className="empty-state tasks-empty">
          <span aria-hidden="true">
            <ListChecks size={28} />
          </span>
          <h2>No hay tareas en esta vista</h2>
          <p>
            Registrá seguimientos operativos para que el equipo tenga un responsable y un próximo
            paso claro.
          </p>
          {canCreate && (
            <button
              className="button button-primary"
              type="button"
              onClick={() => setShowForm(true)}
            >
              <Plus size={17} /> Crear primera tarea
            </button>
          )}
        </section>
      )}
    </div>
  );
}
